import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../assets/Icons";
import SmallPopup from "../components/_utils/CommonSmallPopup";
import CommonToast from "../components/_utils/CommonToast";

const DEFAULT_CONFIG = {
  toastVariant: "var(--error)",
  toastIcon: ICONS.ERROR_WITH_WHITE_CIRCLE,

  popupTitle: "Open in full view?",
  popupSubText:
    "Opening in full view will leave this page and exit the current flow.",
  actionButtonText: "Open in full view",
  confirmButtonText: "Leave",
  cancelButtonText: "Stay",
  actionButtonIcon: ICONS.REDIRECT,
  showPopup: false,
};

const DEFAULT_TOAST_TEXT = "Action restricted in popup mode.";
const DEFAULT_TOAST_TEXT_WITH_ACTION = (
  <>
    Action restricted in popup mode.
    <br />
    Open in full view to perform this action.
  </>
);

/**
 * -----------------------------------------------------------------------------
 * useRestrictedAction
 * -----------------------------------------------------------------------------
 *
 * Displays a restriction toast and, when a redirect path is provided,
 * shows a confirmation popup before navigating to the full-view page.
 *
 * Returns:
 * - showRestrictedAction(options)
 * - RestrictedActionPopup
 *
 * -----------------------------------------------------------------------------
 * Usage
 * -----------------------------------------------------------------------------
 *
 * const {
 *   showRestrictedAction,
 *   RestrictedActionPopup,
 * } = useRestrictedAction();
 *
 * return (
 *   <>
 *   <>{RestrictedActionPopup}</>
 *   </>
 * );
 *
 * -----------------------------------------------------------------------------
 * showRestrictedAction(options)
 * -----------------------------------------------------------------------------
 *
 * @param {Object} [options={}]
 * @param {string} [options.path]
 * Redirect path. When provided, the toast displays an "Open in full view"
 * button. Clicking the button opens a confirmation popup before navigation.
 *
 * @param {string} [options.toastText]
 * Custom toast message.
 *
 * @param {string} [options.toastVariant]
 * Toast background color.
 *
 * @param {string} [options.toastIcon]
 * Toast icon.
 *
 * @param {string} [options.popupTitle]
 * Confirmation popup title.
 *
 * @param {string} [options.popupSubText]
 * Confirmation popup description.
 *
 * @param {string} [options.actionButtonText]
 * Toast action button text.
 *
 * @param {string} [options.actionButtonIcon]
 * Toast action button icon.
 *
 * @param {string} [options.confirmButtonText]
 * Popup confirm button text.
 *
 * @param {string} [options.cancelButtonText]
 * Popup cancel button text.
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 *
 * // Show only the default restriction toast
 * showRestrictedAction();
 *
 * // Show toast with "Open in full view" action
 * showRestrictedAction({
 *   path: "/dashboard",
 * });
 *
 * // Customize the toast message
 * showRestrictedAction({
 *   path: "/dashboard",
 *   toastText: "Editing is available only in full view.",
 * });
 *
 * // Customize the confirmation popup
 * showRestrictedAction({
 *   path: "/dashboard",
 *   popupTitle: "Leave popup?",
 *   popupSubText: "You'll be redirected to the full page.",
 *   confirmButtonText: "Continue",
 *   cancelButtonText: "Stay",
 * });
 *
 * // Fully customized
 * showRestrictedAction({
 *   path: "/dashboard",
 *   toastText: "Associated accounts can only be viewed in full view.",
 *   popupTitle: "Open full view?",
 *   popupSubText: "Leaving this page will exit the current flow.",
 *   actionButtonText: "Open",
 *   confirmButtonText: "Continue",
 *   cancelButtonText: "Cancel",
 * });
 */

const useRestrictedAction = () => {
  const navigate = useNavigate();

  const [redirectData, setRedirectData] = useState({
    path: "",
    state: null,
  });
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const showRestrictedAction = (options = {}) => {
    const { path = "", state = null } = options;

    const hasAction = Boolean(path);

    const popupConfig = {
      ...DEFAULT_CONFIG,
      ...options,
      toastText:
        options.toastText ??
        (hasAction ? DEFAULT_TOAST_TEXT_WITH_ACTION : DEFAULT_TOAST_TEXT),
      showPopup: hasAction,
    };

    setConfig(popupConfig);

    CommonToast({
      text: popupConfig.toastText,
      icon: popupConfig.toastIcon,
      variant: popupConfig.toastVariant,
      buttonNeeded: hasAction,
      btnProps: hasAction
        ? {
            btnText: popupConfig.actionButtonText,
            enabledBgColor: "var(--negative-bg)",
            enabledTextColor: "var(--text-primary)",
            icon: popupConfig.actionButtonIcon,
            btnType: "button",
            isDisabled: false,
            hoverOption: "dark",
            handleFunction: () => {
              toast.dismiss();
              setRedirectData({
                path,
                state,
              });
              setIsPopupOpen(true);
            },
          }
        : undefined,
    });
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const confirmPopup = () => {
    setIsPopupOpen(false);
    if (redirectData.path) {
      navigate(redirectData.path, {
        state: redirectData.state,
      });
    }
  };

  const renderRestrictedActionPopup = () =>
    isPopupOpen ? (
      <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
        <SmallPopup
          headerTitle={config.popupTitle}
          headerFontWeight="semibold"
          headerFontColor="emphasis"
          textSize="font-lg"
          subtext={config.popupSubText}
          btnOneText={config.cancelButtonText}
          btnOneHandleFunction={closePopup}
          btnOneEnabledBgColor="var(--primary-bg)"
          btnOneOutLineColor="var(--text-primary)"
          btnOneEnabledTextColor="var(--text-primary)"
          btnOneHoverOption="white"
          btnTwoText={config.confirmButtonText}
          btnTwoHandleFunction={confirmPopup}
          btnTwoEnabledBgColor="var(--negative-act)"
          btnTwoEnabledTextColor="var(--text-primary)"
          btnTwoIsDisabled={false}
          btnTwoHoverOption="black"
        />
      </div>
    ) : null;

  return {
    showRestrictedAction,
    RestrictedActionPopup: renderRestrictedActionPopup(),
  };
};

export default useRestrictedAction;
